import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import { timeAgo } from '../../utils/dateUtils';

function NotificationBell() {
  const { notifications, markAllRead } = useToast();
  const { role } = useAuth();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const roleNotifications = useMemo(() => notifications.filter((item) => item.role === role), [notifications, role]);
  const unreadCount = useMemo(() => roleNotifications.filter((item) => !item.read).length, [roleNotifications]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) markAllRead();
  };

  const latest = roleNotifications.slice(0, 10);

  return (
    <div className="notification-bell" ref={containerRef}>
      <button type="button" className="notification-bell-btn" onClick={handleToggle} aria-label="Notifications" title="Notifications">
        <span aria-hidden="true">🔔</span>
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>
      {isOpen && (
        <div className="notification-panel">
          <div className="notification-panel-header">{t('notifications.title')}</div>
          <div className="notification-list">
            {latest.length === 0 && <div className="notification-empty">{t('notifications.empty')}</div>}
            {latest.map((item) => (
              <div
                key={item.id}
                className={`notification-item notification-${item.type}${item.link ? ' notification-item-clickable' : ''}`}
                role={item.link ? 'button' : undefined}
                tabIndex={item.link ? 0 : undefined}
                onClick={item.link ? () => { setIsOpen(false); navigate(item.link); } : undefined}
                onKeyDown={item.link ? (event) => { if (event.key === 'Enter') { setIsOpen(false); navigate(item.link); } } : undefined}
              >
                <span className="notification-dot" aria-hidden="true" />
                <div className="notification-item-body">
                  <span className="notification-message">{item.message}</span>
                  <span className="notification-time">{timeAgo(item.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
